<template>
  <v-card elevation="4">
    <v-card-title class="d-flex justify-space-between">
      <span>{{ $t('fleet_health_ui.issue_summary')}}</span>
      <info-modal width="600">
        <v-card-title>
          <h4>{{ $t('fleet_health_ui.issue_summary') }}</h4>
        </v-card-title>
        <v-card-text v-html="$t('fleet_health_ui.modal_info.issue_summary')"/>
      </info-modal>
    </v-card-title>
    <v-card-text v-if="!loading">
      <div class="text-subtitle-2 ml-4">{{ $t('fleet_health_ui.last_30_days') }}</div>
      <v-list flat disabled>
        <v-list-item-group>
          <v-list-item dense>
            <v-list-item-icon><v-icon>mdi-swap-horizontal-circle</v-icon></v-list-item-icon>
            <v-list-item-content class="text-truncate mr-4">
              {{ $t('fleet_health_ui.sensor_configuration_swapped') }}
            </v-list-item-content>
            <span class="warning-count">{{ sensorConfigurationSwappedCount }}</span>
          </v-list-item>
          <v-list-item dense>
            <v-list-item-icon><v-icon color="blue">mdi-thermometer-alert</v-icon></v-list-item-icon>
            <v-list-item-content class="text-truncate mr-4">
              {{ $t('fleet_health_ui.cooler_sensor_failure') }}
            </v-list-item-content>
            <span class="warning-count">{{ coolerSensorFailureCount }}</span>
          </v-list-item>
          <v-list-item dense>
            <v-list-item-icon><v-icon color="indigo darken-2">mdi-thermometer-alert</v-icon></v-list-item-icon>
            <v-list-item-content class="text-truncate mr-4">
              {{ $t('fleet_health_ui.freezer_sensor_failure') }}
            </v-list-item-content>
            <span class="warning-count">{{ freezerSensorFailureCount }}</span>
          </v-list-item>
          <v-list-item dense>
            <v-list-item-icon><v-icon>mdi-map-marker-alert</v-icon></v-list-item-icon>
            <v-list-item-content class="text-truncate mr-4">
              {{ $t('fleet_health_ui.gps_failure') }}
            </v-list-item-content>
            <span class="warning-count">{{ gpsFailureCount }}</span>
          </v-list-item>
          <v-list-item dense>
            <v-list-item-icon><v-icon color="blue">mdi-weather-windy</v-icon></v-list-item-icon>
            <v-list-item-content class="text-truncate mr-4">
              {{ $t('cooler_temperature_health_warning') }}
            </v-list-item-content>
            <span class="warning-count">{{ coolerTemperatureHealthWarningCount }}</span>
          </v-list-item>
          <v-list-item dense>
            <v-list-item-icon><v-icon color="indigo darken-2">mdi-weather-windy</v-icon></v-list-item-icon>
            <v-list-item-content class="text-truncate mr-4">
              {{ $t('freezer_temperature_health_warning') }}
            </v-list-item-content>
            <span class="warning-count">{{ freezerTemperatureHealthWarningCount }}</span>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-card-text>
    <v-card-text v-else>
      <v-skeleton-loader type="list-item" width="50%"/>
      <v-skeleton-loader type="list-item-avatar" v-for="i in 3" :key="i"/>
    </v-card-text>
  </v-card>
</template>

<script>
import VehicleIssueRecord from '@/orm/models/VehicleIssueRecord'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import InfoModal from '@/components/shared/InfoModal'
import { get, filter, flatMap, some } from 'lodash'

export default {
  name: 'VehiclesIssueSummary',
  components: {
    InfoModal
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    fleetHealthData: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    /**
     * Compute the vehicle entries from the fleet health data that have been flagged as unhealthy
     * @return array
     */
    unhealthyVehicles () {
      return flatMap(this.fleetHealthData, data => {
        return get(data, 'unhealthy', [])
      })
    },

    sensorConfigurationSwappedCount () {
      return this.vehiclesWithIssue(RouteViolationRecord.TYPE_SWAPPED_AIR_SENSORS)
    },

    coolerSensorFailureCount () {
      return this.vehiclesWithIssue(VehicleIssueRecord.TYPE_NO_COOLER_SENSOR_DATA)
    },

    freezerSensorFailureCount () {
      return this.vehiclesWithIssue(VehicleIssueRecord.TYPE_NO_FREEZER_SENSOR_DATA)
    },

    gpsFailureCount () {
      return this.vehiclesWithIssue(VehicleIssueRecord.TYPE_NO_GPS_DATA) +
        this.vehiclesWithIssue(VehicleIssueRecord.TYPE_GPS_ZERO)
    },

    coolerTemperatureHealthWarningCount () {
      return this.vehiclesWithIssue('cooler-' + RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED)
    },

    freezerTemperatureHealthWarningCount () {
      return this.vehiclesWithIssue('freezer-' + RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED)
    }

  },
  methods: {
    /**
     * Count the number of vehicles that have at least one unhealthy issue of the specified type
     * @param issueType
     * @returns {number}
     */
    vehiclesWithIssue (issueType) {
      return filter(this.unhealthyVehicles, vehicle => {
        return some(vehicle.issues, { type: issueType, status: 'unhealthy' })
      }).length
    }
  }
}
</script>

<style scoped lang="scss">
  .warning-count {
    font-size: 18px;
  }
</style>
